import { motion } from "framer-motion";
import { Building2, Cpu, Cloud, Wallet, ServerCrash, Lock, ArrowRight, Server, Network } from "lucide-react";

const PROBLEMS = [
    { icon: Building2, title: "Cloud Monopolies Control Deployment", body: "A few companies decide where applications live and how much developers pay." },
    { icon: Cpu, title: "AI Compute Is Centralized", body: "GPU access is expensive, scarce, and controlled by hyperscalers." },
    { icon: Cloud, title: "Web3 Still Runs on Web2 Clouds", body: "Many dApps, RPCs, frontends, and metadata layers still depend on centralized infrastructure." },
    { icon: Wallet, title: "Developers Pay Forever", body: "Builders create value, but centralized platforms capture the infrastructure margin." },
    { icon: Lock, title: "Data Sovereignty Is Broken", body: "User and enterprise data sits inside private cloud infrastructure." },
    { icon: ServerCrash, title: "Outages Break the Web", body: "One provider failure can affect thousands of applications." },
];

export default function Problem() {
    return (
        <section className="relative py-16 sm:py-24 md:py-32 lg:py-40 border-t border-line overflow-hidden" data-testid="problem-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 items-start mb-12 sm:mb-16">
                    <div className="lg:col-span-5">
                        <div className="overline mb-4">01 — The Problem</div>
                        <h2 className="font-heading font-black text-[1.85rem] sm:text-4xl lg:text-5xl leading-[1.06] tracking-tight text-ink">
                            The Cloud Became<br />
                            <span className="text-muted-ink">the Internet's Biggest</span><br />
                            Bottleneck.
                        </h2>
                        <p className="mt-5 sm:mt-6 text-[15px] sm:text-base text-muted-ink max-w-xl leading-relaxed">
                            The modern internet depends on a few centralized cloud giants. They control deployment, pricing, access, data custody, AI compute, and infrastructure availability.
                        </p>
                        <p className="mt-4 text-[15px] sm:text-base text-muted-ink max-w-xl leading-relaxed">
                            Web3 and AI need a new infrastructure layer — open, programmable, and owned by the people who power it.
                        </p>
                    </div>

                    {/* Migration visual: Centralized → DeCloudX */}
                    <div className="lg:col-span-7 relative" data-testid="migration-visual">
                        <div className="surface rounded-3xl p-5 sm:p-6 md:p-8 relative overflow-hidden">
                            <div className="absolute inset-0 dot-grid opacity-30" />
                            <div className="relative grid grid-cols-12 gap-2 sm:gap-3 items-center min-h-[260px] sm:min-h-[280px]">
                                {/* Left: centralized stack */}
                                <div className="col-span-5">
                                    <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-faint-ink mb-3 text-center">Centralized Cloud</div>
                                    <div className="space-y-2">
                                        {["AWS", "GCP", "AZURE"].map((n, i) => (
                                            <div key={i} className="surface rounded-lg p-2 sm:p-3 flex items-center gap-2 opacity-90">
                                                <Cloud size={12} className="text-slate-400 shrink-0" strokeWidth={1.5} />
                                                <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-muted-ink">{n}</span>
                                            </div>
                                        ))}
                                        <div className="px-2 sm:px-3 py-2 rounded-lg bg-card-muted border border-line">
                                            <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-faint-ink mb-1">Locked Beneath</div>
                                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                                {["dApps","AI","Devs","Data"].map((x,j) => (
                                                    <span key={j} className="font-mono text-[8px] sm:text-[9px] uppercase tracking-wider text-muted-ink inline-flex items-center gap-0.5 sm:gap-1">
                                                        <Lock size={7} strokeWidth={2} />{x}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Middle: arrow */}
                                <div className="col-span-2 flex flex-col items-center justify-center gap-2">
                                    <div className="relative w-full h-px bg-gradient-to-r from-slate-300 via-brand-cyan/60 to-brand-violet/60" />
                                    <ArrowRight size={18} className="text-brand-blue sm:!w-[22px] sm:!h-[22px]" strokeWidth={1.5} />
                                    <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-brand-blue text-center">Migrate</div>
                                </div>

                                {/* Right: DeCloudX mesh */}
                                <div className="col-span-5">
                                    <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-brand-cyan mb-3 text-center">Open DePIN Network</div>
                                    <div className="relative aspect-[5/4] rounded-2xl overflow-hidden surface">
                                        <div className="absolute inset-0 bg-gradient-to-br from-brand-violet/10 via-brand-blue/5 to-brand-cyan/10" />
                                        {/* Scattered nodes */}
                                        {[
                                            { t: "12%", l: "20%", c: "#06B6D4" },
                                            { t: "28%", l: "65%", c: "#8B5CF6" },
                                            { t: "45%", l: "30%", c: "#2563EB" },
                                            { t: "55%", l: "78%", c: "#06B6D4" },
                                            { t: "70%", l: "15%", c: "#8B5CF6" },
                                            { t: "78%", l: "55%", c: "#2563EB" },
                                            { t: "35%", l: "48%", c: "#172554" },
                                        ].map((p, k) => (
                                            <div key={k} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ top: p.t, left: p.l }}>
                                                <div className="w-2 h-2 rounded-full" style={{ background: p.c, boxShadow: `0 0 8px ${p.c}` }} />
                                            </div>
                                        ))}
                                        {/* Center pulse */}
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-16 h-16 rounded-full brand-gradient-bg opacity-90 flex items-center justify-center text-white animate-pulse-glow shadow-lg">
                                                <Network size={20} strokeWidth={1.5} />
                                            </div>
                                        </div>
                                        <div className="absolute bottom-2 left-2 right-2 flex justify-between font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-faint-ink">
                                            <span>14k+ nodes</span>
                                            <span>62 regions</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {PROBLEMS.map((p, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            className="surface card-lift p-5 sm:p-7 rounded-2xl"
                            data-testid={`problem-card-${i}`}
                        >
                            <div className="w-10 h-10 rounded-xl bg-card-muted border border-line flex items-center justify-center mb-4 sm:mb-5">
                                <p.icon size={18} className="text-muted-ink" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading font-bold text-base sm:text-lg text-ink mb-2">{p.title}</h3>
                            <p className="text-[13.5px] sm:text-sm text-muted-ink leading-relaxed">{p.body}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
